/**
 * Frame-time statistics for the log channel and the Status window.
 *
 * Engine-free: `frame(now)` records one frame interval, `snapshot()` folds the
 * window into a sample and starts a new one. `src/index.ts` adds what only
 * the engine knows (draw calls, triangles, sub-draws, hit-test cost, the
 * session facts) and logs the result as one `[Atlas] perf` line every few
 * seconds while presenting, so a headset session reads on the desk as a
 * series of numbers rather than a feeling. Nothing here is stored anywhere:
 * the line goes wherever the remote logger points (docs/11-LOGGING_AND_PERFORMANCE.md).
 */
export interface FrameStats {
  /** Frames in the window. */
  frames: number;
  fps: number;
  avgMs: number;
  p50Ms: number;
  p95Ms: number;
  maxMs: number;
  /** Frames over `slowMs` in the window: the stalls a person feels as a hitch. */
  slow: number;
}

export interface PerfSample extends FrameStats {
  /** When the window closed, milliseconds since the page loaded. */
  at: number;
  presenting: boolean;
  multiview: boolean;
  /** The session's refresh rate when known, else null. */
  sessionHz: number | null;
  drawCalls: number;
  triangles: number;
  /** Sub-draws submitted for the last frame, summed over the bodies. */
  subDraws: number;
  instances: number;
  culled: number;
  bodies: number;
  level: string;
  /** Ray queries answered in the window and their total cost. */
  hitRays: number;
  hitMs: number;
  /** Average cost of `interactions.runtime.update()` per frame in the window. */
  runtimeMs: number;
  /** JavaScript heap in use (Chromium only), else null. */
  heapMb: number | null;
  /** Requests the remote logger has put on the wire this page load. */
  logRequests: number;
}

export function percentile(sorted: readonly number[], p: number): number {
  if (sorted.length === 0) return 0;
  return sorted[Math.min(sorted.length - 1, Math.floor(p * sorted.length))]!;
}

export class PerfMonitor {
  private intervals: number[] = [];
  private lastFrameAt = -1;
  private runtimeMsTotal = 0;
  private runtimeMsFrames = 0;

  constructor(
    /** A frame longer than this counts as a stall; 33 ms is two 60 Hz frames or three at 90 Hz. */
    readonly slowMs = 33,
    /** Frames kept per window at most; a window is closed by `snapshot()`. */
    readonly maxFrames = 2000,
  ) {}

  /** One frame happened at `now` (milliseconds); the first call only starts the clock. */
  frame(now: number, runtimeMs = 0): void {
    if (this.lastFrameAt >= 0) {
      if (this.intervals.length < this.maxFrames) this.intervals.push(now - this.lastFrameAt);
      this.runtimeMsTotal += runtimeMs;
      this.runtimeMsFrames += 1;
    }
    this.lastFrameAt = now;
  }

  /** Forget the window without reporting it: a session just started, the numbers before it are not its own. */
  reset(now = -1): void {
    this.intervals = [];
    this.lastFrameAt = now;
    this.runtimeMsTotal = 0;
    this.runtimeMsFrames = 0;
  }

  /** Fold the window into statistics and start a new window. */
  snapshot(): FrameStats & { runtimeMs: number } {
    const sorted = [...this.intervals].sort((a, b) => a - b);
    const frames = sorted.length;
    const total = sorted.reduce((a, b) => a + b, 0);
    const avgMs = frames ? total / frames : 0;
    const stats = {
      frames,
      fps: avgMs > 0 ? +(1000 / avgMs).toFixed(1) : 0,
      avgMs: +avgMs.toFixed(2),
      p50Ms: +percentile(sorted, 0.5).toFixed(2),
      p95Ms: +percentile(sorted, 0.95).toFixed(2),
      maxMs: +(frames ? sorted[frames - 1]! : 0).toFixed(2),
      slow: sorted.filter((ms) => ms > this.slowMs).length,
      runtimeMs: this.runtimeMsFrames ? +(this.runtimeMsTotal / this.runtimeMsFrames).toFixed(3) : 0,
    };
    this.intervals = [];
    this.runtimeMsTotal = 0;
    this.runtimeMsFrames = 0;
    return stats;
  }
}

/** One line for the Status window: the numbers a tester reads aloud. */
export function perfLine(s: PerfSample | null): string {
  if (!s) return "perf: no sample yet";
  const heap = s.heapMb === null ? "" : ` - heap ${s.heapMb} MB`;
  return `${s.fps} fps - ${s.avgMs} ms (p95 ${s.p95Ms}, max ${s.maxMs}) - ${s.drawCalls} calls, ${s.subDraws} sub-draws - ${s.triangles.toLocaleString()} tris - hits ${s.hitRays} in ${s.hitMs} ms${heap}`;
}
